import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useOFXImport } from '@/hooks/useOFXImport';
import { useFinancialAccounts } from '@/hooks/useFinancialAccounts';
import { detectBankFromOFX } from '@/utils/ofxParser';

interface OFXImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedAccountId?: string;
}

export function OFXImportDialog({ open, onOpenChange, preselectedAccountId }: OFXImportDialogProps) {
  const [selectedAccountId, setSelectedAccountId] = useState<string>(preselectedAccountId || '');
  const [error, setError] = useState<string | null>(null);
  
  const { 
    parseFile, 
    parsedStatement, 
    clearParsedStatement, 
    importTransactionsAsync, 
    isImporting,
    importResult,
  } = useOFXImport();
  
  const { accounts } = useFinancialAccounts();
  
  // Filter only bank accounts
  const bankAccounts = accounts?.filter(a => a.account_type === 'bank') || [];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError(null);
    
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    
    try {
      await parseFile(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar arquivo');
    }
  }, [parseFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/x-ofx': ['.ofx'],
      'text/plain': ['.ofx'],
    },
    maxFiles: 1,
  });

  const handleImport = async () => {
    if (!parsedStatement || !selectedAccountId) return;
    
    try {
      await importTransactionsAsync({
        statement: parsedStatement,
        financialAccountId: selectedAccountId,
      });
    } catch (err) {
      // Error handled by the hook
    }
  };

  const handleClose = () => {
    clearParsedStatement();
    setError(null);
    setSelectedAccountId(preselectedAccountId || '');
    onOpenChange(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const detectedBank = parsedStatement ? detectBankFromOFX(parsedStatement) : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Importar Extrato OFX
          </DialogTitle>
          <DialogDescription>
            Importe transações de um arquivo OFX para conciliação bancária
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden space-y-4">
          {/* Success result */}
          {importResult && (
            <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700 dark:text-green-400">
                <strong>Importação concluída!</strong>
                <br />
                {importResult.imported} transações importadas
                {importResult.duplicates > 0 && `, ${importResult.duplicates} duplicadas ignoradas`}
                {importResult.errors > 0 && `, ${importResult.errors} erros`}
              </AlertDescription>
            </Alert>
          )}

          {/* File dropzone */}
          {!parsedStatement && !importResult && (
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors
                ${isDragActive 
                  ? 'border-primary bg-primary/5' 
                  : 'border-muted-foreground/25 hover:border-primary/50'}
              `}
            >
              <input {...getInputProps()} />
              <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
              {isDragActive ? (
                <p className="text-primary font-medium">Solte o arquivo aqui...</p>
              ) : (
                <>
                  <p className="font-medium">Arraste um arquivo OFX ou clique para selecionar</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Suporta arquivos .OFX de todos os bancos brasileiros
                  </p>
                </>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Parsed statement preview */}
          {parsedStatement && !importResult && (
            <>
              {/* Statement info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Banco</Label>
                  <p className="font-medium">
                    {detectedBank ? detectedBank.toUpperCase() : `Código: ${parsedStatement.bankId}`}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Conta</Label>
                  <p className="font-medium">{parsedStatement.accountId}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Período</Label>
                  <p className="font-medium">
                    {format(parsedStatement.startDate, 'dd/MM/yyyy', { locale: ptBR })} -{' '}
                    {format(parsedStatement.endDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Saldo</Label>
                  <p className="font-medium">{formatCurrency(parsedStatement.ledgerBalance)}</p>
                </div>
              </div>

              {/* Account selection */}
              <div className="space-y-2">
                <Label>Conta de destino</Label>
                <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta bancária" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.account_name}
                        {account.bank_name && ` - ${account.bank_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Transactions preview */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Transações encontradas
                  <Badge variant="secondary">{parsedStatement.transactions.length}</Badge>
                </Label>
                <ScrollArea className="h-[200px] border rounded-lg">
                  <div className="divide-y">
                    {parsedStatement.transactions.map((tx, index) => (
                      <div 
                        key={tx.id || index} 
                        className="flex items-center justify-between p-3 hover:bg-muted/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{tx.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(tx.date, 'dd/MM/yyyy', { locale: ptBR })} • {tx.type}
                          </p>
                        </div>
                        <p className={`font-medium text-sm ${
                          tx.amount >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(tx.amount)}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {importResult ? 'Fechar' : 'Cancelar'}
          </Button>
          
          {parsedStatement && !importResult && (
            <Button 
              onClick={handleImport} 
              disabled={!selectedAccountId || isImporting}
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Importar {parsedStatement.transactions.length} Transações
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
